import { ReactElement } from 'react';
import type { Dealer } from '@makemydeal/dr-shared-types';

type ComponentFlowConfig = {
    name: string;
    routingComponent: () => ReactElement;
};

type DashComponentFlowConfigs = {
    changeVehicle: ComponentFlowConfig;
    creditBureau: ComponentFlowConfig;
    dealHistory: ComponentFlowConfig;
    desking: ComponentFlowConfig;
    trade: ComponentFlowConfig;
    accessoriesCatalog: ComponentFlowConfig;
    creditDecision: ComponentFlowConfig;
    ddj: ComponentFlowConfig;
    auditLog: ComponentFlowConfig;
};

export type DashComponentConfigParams = {
    dealer: Partial<Dealer>;
    configurations: DashComponentFlowConfigs;
    /**
     * True once trade's selectors exist. `configureTradeIn()` creates them on BOOTSTRAP_SUCCESS, but
     * only when the init payload carries both `staticImages` and `services.gateway` — so bootstrap
     * succeeding is necessary and not sufficient. Mounting trade earlier throws in
     * `TradeInContainer.mapStateToProps`.
     */
    isTradeReady: boolean;
};

export const useDashComponentConfig = ({ dealer, configurations, isTradeReady }: DashComponentConfigParams) => {
    const componentConfigs = {
        [configurations.changeVehicle.name]: configurations.changeVehicle,
        [configurations.creditBureau.name]: configurations.creditBureau,
        [configurations.dealHistory.name]: configurations.dealHistory,
        [configurations.desking.name]: configurations.desking,
        [configurations.trade.name]: configurations.trade,
        [configurations.accessoriesCatalog.name]: configurations.accessoriesCatalog,
        [configurations.creditDecision.name]: configurations.creditDecision,
        [configurations.ddj.name]: configurations.ddj,
        [configurations.auditLog.name]: configurations.auditLog
    };

    const buildComponentFromConfig = (configName: string) => {
        const config = componentConfigs[configName];
        return <config.routingComponent />;
    };

    // Generate routing components
    const changeVehicleComponent = buildComponentFromConfig(configurations.changeVehicle.name);
    const dashboardComponent = buildComponentFromConfig(configurations.desking.name);
    const dealHistoryComponent = buildComponentFromConfig(configurations.dealHistory.name);
    const creditBureauComponent = buildComponentFromConfig(configurations.creditBureau.name);
    const creditDecisionComponent = buildComponentFromConfig(configurations.creditDecision.name);
    const auditLogComponent = buildComponentFromConfig(configurations.auditLog.name);

    // Readiness-gated: see `isTradeReady` on DashComponentConfigParams.
    const tradeComponent = isTradeReady ? buildComponentFromConfig(configurations.trade.name) : null;

    // Dealer-gated components
    const accessoriesCatalogComponent = dealer.isAccessoriesCatalogEnabled
        ? buildComponentFromConfig(configurations.accessoriesCatalog.name)
        : null;

    const ddjComponent = dealer.enableDigitalDealJacket ? buildComponentFromConfig(configurations.ddj.name) : null;

    return {
        changeVehicleComponent,
        dashboardComponent,
        dealHistoryComponent,
        creditBureauComponent,
        creditDecisionComponent,
        tradeComponent,
        accessoriesCatalogComponent,
        ddjComponent,
        auditLogComponent
    };
};
